import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Clock, 
  DollarSign, 
  User, 
  Calendar, 
  CheckCircle, 
  XCircle, 
  Eye,
  Filter,
  Search,
  ChevronDown
} from 'lucide-react';
import { useApiClient } from '../hooks/useApiClient';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ErrorMessage } from '../components/ui/ErrorMessage';
import { ApprovalModal } from '../components/approvals/ApprovalModal';
import { formatCurrency, formatDate } from '../utils/formatters';
import { useToast } from '../components/ui/Toast';

interface PendingApproval {
  id: string;
  expense_id: string;
  expense_description: string;
  expense_amount: number;
  expense_currency: string;
  expense_date: string;
  expense_category: string | null;
  submitted_by: string;
  submitted_at: string | null;
  approval_level: number;
  created_at: string;
}

interface ApprovalFilters {
  search: string;
  category: string;
  amountRange: string;
  dateRange: string;
}

export const ApprovalQueue: React.FC = () => {
  const [approvals, setApprovals] = useState<PendingApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedApproval, setSelectedApproval] = useState<PendingApproval | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [rowLoadingId, setRowLoadingId] = useState<string | null>(null);
  const [filters, setFilters] = useState<ApprovalFilters>({
    search: '',
    category: '',
    amountRange: '',
    dateRange: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedApprovals, setSelectedApprovals] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const apiClient = useApiClient();
  const { toast } = useToast();

  const fetchApprovals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get('/approvals/pending');
      setApprovals(response || []);
    } catch (err: any) {
      console.error('Failed to fetch pending approvals:', err);
      setError(err.response?.data?.detail || 'Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return; // guard StrictMode double invoke
    loadedRef.current = true;
    fetchApprovals();
  }, [fetchApprovals]);

  const handleApprovalAction = async (approvalId: string, action: 'approve' | 'reject', comments?: string) => {
    try {
      const endpoint = action === 'approve' ? 'approve' : 'reject';
      const payload = action === 'reject' ? { comments } : { comments };
      
      await apiClient.post(`/approvals/${approvalId}/${endpoint}`, payload);
      
      // Remove the approval from the list
      setApprovals(prev => prev.filter(approval => approval.id !== approvalId));
      
      // Close modal
      setShowModal(false);
      setSelectedApproval(null);
      
      // success toast
      toast.success(
        action === 'approve' ? 'Expense approved successfully.' : 'Expense rejected successfully.'
      );
      
    } catch (err: any) {
      console.error(`Failed to ${action} expense:`, err);
      // Propagate for modal, but also provide a readable message
      const msg = err.response?.data?.detail || `Failed to ${action} expense`;
      toast.error(msg);
      throw new Error(msg);
    }
  };

  // Quick action handlers for table row icons (do not throw; show error banner)
  const handleQuickApprove = async (approvalId: string) => {
    try {
      setRowLoadingId(approvalId);
      setError(null);
      await apiClient.post(`/approvals/${approvalId}/approve`, { comments: undefined });
      setApprovals(prev => prev.filter(a => a.id !== approvalId));
      // approved toast
      toast.success('Expense approved.');
    } catch (err: any) {
      const msg: string = err?.response?.data?.detail || 'Failed to approve expense';
      setError(msg);
      toast.error(msg);
    } finally {
      setRowLoadingId(null);
    }
  };

  const handleQuickReject = async (approvalId: string) => {
    const comments = prompt('Enter rejection comments:');
    if (!comments) return;
    try {
      setRowLoadingId(approvalId);
      setError(null);
      await apiClient.post(`/approvals/${approvalId}/reject`, { comments });
      setApprovals(prev => prev.filter(a => a.id !== approvalId));
      // rejected toast
      toast.success('Expense rejected.');
    } catch (err: any) {
      const msg: string = err?.response?.data?.detail || 'Failed to reject expense';
      setError(msg);
      toast.error(msg);
    } finally {
      setRowLoadingId(null);
    }
  };

  const handleBulkAction = async (action: 'approve' | 'reject', comments?: string) => {
    if (selectedApprovals.size === 0) return;

    try {
      setBulkActionLoading(true);
      
      const approvalIds = Array.from(selectedApprovals);
      await apiClient.post('/approvals/bulk-action', {
        approval_ids: approvalIds,
        action,
        comments
      });

      // Remove approved/rejected items from the list
      setApprovals(prev => prev.filter(approval => !selectedApprovals.has(approval.id)));
      setSelectedApprovals(new Set());
      // bulk done
      
    } catch (err: any) {
      console.error(`Failed to perform bulk ${action}:`, err);
      setError(err.response?.data?.detail || `Failed to perform bulk ${action}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const toggleApprovalSelection = (approvalId: string) => {
    setSelectedApprovals(prev => {
      const newSet = new Set(prev);
      if (newSet.has(approvalId)) {
        newSet.delete(approvalId);
      } else {
        newSet.add(approvalId);
      }
      return newSet;
    });
  };

  const selectAllApprovals = () => {
    if (selectedApprovals.size === filteredApprovals.length) {
      setSelectedApprovals(new Set());
    } else {
      setSelectedApprovals(new Set(filteredApprovals.map(a => a.id)));
    }
  };

  const filteredApprovals = approvals.filter(approval => {
    if (filters.search && !approval.expense_description.toLowerCase().includes(filters.search.toLowerCase()) &&
        !approval.submitted_by.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    
    if (filters.category && approval.expense_category !== filters.category) {
      return false;
    }
    
    if (filters.amountRange) {
      const amount = approval.expense_amount;
      switch (filters.amountRange) {
        case 'under-100':
          if (amount >= 100) return false;
          break;
        case '100-500':
          if (amount < 100 || amount >= 500) return false;
          break;
        case '500-1000':
          if (amount < 500 || amount >= 1000) return false;
          break;
        case 'over-1000':
          if (amount < 1000) return false;
          break;
      }
    }
    
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Approval Queue</h1>
            <p className="mt-2 text-gray-600">
              Review and approve pending expense submissions
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            {selectedApprovals.size > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {selectedApprovals.size} selected
                </span>
                <button
                  onClick={() => handleBulkAction('approve')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  Bulk Approve
                </button>
                <button
                  onClick={() => {
                    const comments = prompt('Enter rejection comments:');
                    if (comments) handleBulkAction('reject', comments);
                  }}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  Bulk Reject
                </button>
              </div>
            )}
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
              <ChevronDown className={`h-4 w-4 transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-orange-500" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{filteredApprovals.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <DollarSign className="h-8 w-8 text-green-500" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Amount</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(filteredApprovals.reduce((sum, a) => sum + a.expense_amount, 0), 'USD')}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <User className="h-8 w-8 text-blue-500" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Submitters</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(filteredApprovals.map(a => a.submitted_by)).size}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-purple-500" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Avg Days Pending</p>
                <p className="text-2xl font-bold text-gray-900">
                  {filteredApprovals.length > 0 
                    ? Math.round(filteredApprovals.reduce((sum, a) => {
                        const days = Math.floor((Date.now() - new Date(a.created_at).getTime()) / (1000 * 60 * 60 * 24));
                        return sum + days;
                      }, 0) / filteredApprovals.length)
                    : 0
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  placeholder="Search description or submitter..."
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={filters.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                <option value="Travel">Travel</option>
                <option value="Meals">Meals</option>
                <option value="Office Supplies">Office Supplies</option>
                <option value="Software">Software</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount Range
              </label>
              <select
                value={filters.amountRange}
                onChange={(e) => setFilters(prev => ({ ...prev, amountRange: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Amounts</option>
                <option value="under-100">Under $100</option>
                <option value="100-500">$100 - $500</option>
                <option value="500-1000">$500 - $1,000</option>
                <option value="over-1000">Over $1,000</option>
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ search: '', category: '', amountRange: '', dateRange: '' })}
                className="w-full px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6">
          <ErrorMessage message={error} />
        </div>
      )}

      {/* Approvals Table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        {filteredApprovals.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No pending approvals</h3>
            <p className="mt-1 text-sm text-gray-500">
              {approvals.length === 0 
                ? "There are no expenses waiting for your approval."
                : "No approvals match your current filters."
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedApprovals.size === filteredApprovals.length && filteredApprovals.length > 0}
                      onChange={selectAllApprovals}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expense
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredApprovals.map((approval) => (
                  <tr key={approval.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedApprovals.has(approval.id)}
                        onChange={() => toggleApprovalSelection(approval.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {approval.expense_description}
                        </div>
                        <div className="text-sm text-gray-500">
                          {approval.expense_category || 'Uncategorized'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(approval.expense_amount, approval.expense_currency)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(approval.expense_date)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{approval.submitted_by}</div>
                      <div className="text-sm text-gray-500">
                        {approval.submitted_at ? formatDate(approval.submitted_at) : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {Math.floor((Date.now() - new Date(approval.created_at).getTime()) / (1000 * 60 * 60 * 24))} days ago
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Level {approval.approval_level}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedApproval(approval);
                            setShowModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleQuickApprove(approval.id)}
                          className="text-green-600 hover:text-green-900 disabled:opacity-50"
                          disabled={rowLoadingId === approval.id}
                          title="Quick Approve"
                        >
                          {rowLoadingId === approval.id ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <CheckCircle className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleQuickReject(approval.id)}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          disabled={rowLoadingId === approval.id}
                          title="Quick Reject"
                        >
                          {rowLoadingId === approval.id ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <XCircle className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {showModal && selectedApproval && (
        <ApprovalModal
          approval={selectedApproval}
          onApprove={(comments) => handleApprovalAction(selectedApproval.id, 'approve', comments)}
          onReject={(comments) => handleApprovalAction(selectedApproval.id, 'reject', comments)}
          onClose={() => {
            setShowModal(false);
            setSelectedApproval(null);
          }}
        />
      )}
    </div>
  );
};
